// The cards of the workflow page: the prompt with its trigger, guard and resources, the
// recent runs, the tool policy the run's capability token is minted from, and the last 30 days.
import { CheckCircle, SealCheck } from "@phosphor-icons/react";
import { Link } from "react-router";

import type { Run, Workflow } from "../../api/client";
import { type Messages, useT } from "../../i18n";
import { Field, WidgetCard } from "../../ui/Card";
import { type Column, DataTable } from "../../ui/DataTable";
import { EmptyState } from "../../ui/states";
import { DotStatus, Mono, Pill } from "../../ui/status";
import { runTone, statusLabel } from "../../ui/tones";
import { dayTime, elapsed, gibCount, usd } from "../format";
import { parseTrigger, policyActions, triggerLabel } from "../policy";
import type { WorkflowStats } from "../runs";

export function PromptCard({ workflow }: { workflow: Workflow }) {
  const t = useT().workloads.workflow;
  const p = t.prompt;
  return (
    <WidgetCard title={p.title} description={p.description}>
      <div className="whitespace-pre-wrap break-words rounded-lg border border-muted bg-gray-50 p-4 text-[14px] leading-relaxed text-gray-800 dark:bg-gray-100/50">
        {workflow.prompt}
      </div>
      <dl className="m-0 mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label={p.trigger}>{triggerLabel(parseTrigger(workflow.trigger))}</Field>
        <Field label={p.guard} mono>
          <span title={workflow.guard_script ?? undefined}>{workflow.guard_script || p.none}</span>
        </Field>
        <Field label={p.docker}>{workflow.needs_docker ? p.dindSidecar : p.notNeeded}</Field>
        <Field label={p.worker}>
          {workflow.cpus} CPU · {gibCount(workflow.memory_bytes)} GiB
        </Field>
        <Field label={p.timeout}>{t.minutes(workflow.timeout_minutes)}</Field>
        <Field label={p.concurrency}>
          {p.concurrencyValue(workflow.max_concurrency, workflow.hold_minutes)}
        </Field>
      </dl>
    </WidgetCard>
  );
}

const runColumns = (c: Messages["workloads"]["runs"]["columns"]): Column<Run>[] => [
  {
    key: "id",
    header: c.run,
    cell: (r) => (
      <Link to={`/runs/${r.id}`} className="font-mono text-[12.5px] text-primary" title={r.id}>
        {r.id.slice(0, 10)}…
      </Link>
    ),
  },
  { key: "status", header: c.status, cell: (r) => <DotStatus tone={runTone(r.status)} label={<span className="first-letter:uppercase inline-block">{statusLabel(r.status)}</span>} /> },
  { key: "queued", header: c.queued, cell: (r) => <span className="whitespace-nowrap">{dayTime(r.queued_at)}</span>, hideBelow: "sm" },
  { key: "duration", header: c.duration, cell: (r) => <span className="tabular">{elapsed(r.started_at, r.finished_at)}</span>, hideBelow: "md" },
  { key: "cost", header: c.cost, cell: (r) => <span className="tabular font-medium text-gray-900">{usd(r.cost_usd)}</span> },
];

export function RecentRunsCard({ runs }: { runs: Run[] }) {
  const { workloads } = useT();
  const t = workloads.workflow.recent;
  return (
    <WidgetCard
      title={t.title}
      action={
        <Link to="/runs" className="text-[13px] font-medium text-primary">
          {t.allRuns}
        </Link>
      }
    >
      {runs.length === 0 ? (
        <EmptyState compact title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : (
        <DataTable label={t.title} columns={runColumns(workloads.runs.columns)} rows={runs.slice(0, 10)} rowKey={(r) => r.id} />
      )}
    </WidgetCard>
  );
}

export function ToolPolicyCard({ workflow }: { workflow: Workflow }) {
  const t = useT().workloads.workflow.policy;
  const actions = policyActions(workflow.tool_policy);
  return (
    <WidgetCard title={t.title} description={t.description}>
      {actions.length === 0 ? (
        <EmptyState compact title={t.emptyTitle}>
          {t.emptyBody}
        </EmptyState>
      ) : (
        <ul className="m-0 list-none divide-y divide-muted p-0">
          {actions.map((a) => (
            <li key={a.action} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="font-mono text-[13px] font-medium text-gray-900">{a.action}</div>
                {a.scope ? <div className="truncate text-xs text-gray-500">{a.scope}</div> : null}
              </div>
              {a.requiresApproval ? (
                <Pill tone="orange" icon={<SealCheck weight="duotone" />}>
                  {t.deviceApproval}
                </Pill>
              ) : (
                <Pill tone="green" icon={<CheckCircle weight="duotone" />}>
                  {t.allowed}
                </Pill>
              )}
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

export function ThirtyDaysCard({ stats, workflow }: { stats: WorkflowStats; workflow: Workflow }) {
  const t = useT().workloads.workflow.thirty;
  const pct = stats.successPct;
  return (
    <WidgetCard title={t.title}>
      <dl className="m-0 grid grid-cols-3 gap-4">
        <Field label={t.runs}>{stats.runs}</Field>
        <Field label={t.succeeded}>{pct === undefined ? "—" : `${pct}%`}</Field>
        <Field label={t.avgCost}>{usd(stats.avgCost)}</Field>
      </dl>
      {pct !== undefined ? (
        <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-gray-100">
          <div className="h-full bg-green" style={{ width: `${pct}%` }} />
          <div className="h-full bg-red" style={{ width: `${100 - pct}%` }} />
        </div>
      ) : null}
      <div className="mt-2 text-xs text-gray-500">
        {t.sessionReuse(workflow.session_reuse_window_minutes)} · <Mono className="text-[11.5px]">needs_docker={String(workflow.needs_docker)}</Mono>
      </div>
    </WidgetCard>
  );
}
